import { html, property, queryAssignedNodes, state } from 'lit-element';
import { nothing } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map';
import { ifDefined } from 'lit-html/directives/if-defined';
import {
  event,
  EventEmitter,
  KatLitElement,
  register,
} from '../../shared/base';
import { isPageDirectionRightToLeft } from '../../utils/rtl-utils';
import { getViewport } from '../../utils/viewport-utils';
import { KatButton } from '../button';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './button-group.lit.scss';
import getString from './strings';

/**
 * @component {kat-button-group} KatalButtongroup Button groups display multiple selectable options adjacent to one another.
 * @status production
 * @theme flo
 * @slot default Should be filled with `kat-button` elements, of which only one can be selected at a time. Each `kat-button` should have a `value` attribute.
 * @guideline Do Each button provided is a separate option.
 * @example SentimentChoice {"value": "", "disabled": false, "content": "<kat-button label='Strongly Agree' value=\"strong_agree\"><\/kat-button>\r\n<kat-button label='Agree' value=\"agree\"><\/kat-button>\r\n<kat-button label='Neutral' value=\"neutral\"><\/kat-button>\r\n<kat-button label='Disagree' value=\"disagree\"><\/kat-button>\r\n<kat-button label='Strongly Disagree' value=\"strong_disagree\"><\/kat-button>"}
 * @example SentimentChoicePreselected {"value": "neutral", "disabled": false, "content": "<kat-button label='Strongly Agree' value=\"strong_agree\"><\/kat-button>\r\n<kat-button label='Agree' value=\"agree\"><\/kat-button>\r\n<kat-button label='Neutral' value=\"neutral\"><\/kat-button>\r\n<kat-button label='Disagree' value=\"disagree\"><\/kat-button>\r\n<kat-button label='Strongly Disagree' value=\"strong_disagree\"><\/kat-button>"}
 * @example SentimentChoicePreselectedDisabled {"value": "neutral", "disabled": true, "content": "<kat-button label='Strongly Agree' value=\"strong_agree\"><\/kat-button>\r\n<kat-button label='Agree' value=\"agree\"><\/kat-button>\r\n<kat-button label='Neutral' value=\"neutral\"><\/kat-button>\r\n<kat-button label='Disagree' value=\"disagree\"><\/kat-button>\r\n<kat-button label='Strongly Disagree' value=\"strong_disagree\"><\/kat-button>"}
 * @example SentimentChoiceOverflow {"value": "", "disabled": false, "content": "<kat-button label='Strongly Agree' value=\"strong_agree\"><\/kat-button>\r\n<kat-button label='Agree' value=\"agree\"><\/kat-button>\r\n<kat-button label='Neutral' value=\"neutral\"><\/kat-button>\r\n<kat-button label='Disagree' value=\"disagree\"><\/kat-button>\r\n<kat-button label='Strongly Disagree' value=\"strong_disagree\"><\/kat-button>\r\n<div slot=\"overflow\">\r\n<kat-button label='No Answer' value=\"no_answer\"><\/kat-button>\r\n<kat-button label='Skipping' value=\"skipping\"><\/kat-button><\/div>"}
 * @a11y {keyboard}
 * @a11y {contrast}
 */
@register('kat-button-group')
export class KatButtonGroup extends KatLitElement {
  /**
   * Whether element is disabled.
   * @type {boolean}
   */
  @property()
  disabled?: boolean;

  /**
   * Current value based on selected button.
   * @type {string}
   */
  @property()
  value?: string;

  /**
   * Whether element overflow is expanded.
   * @type {boolean}
   */
  @property()
  get expanded(): boolean {
    return this._isExpanded;
  }

  set expanded(val: boolean) {
    this._isExpanded = val;
    (val ? this._expand : this._collapse).emit();
  }

  /**
   * Locale to identify the language used in fixed labels.
   * Use this property/attribute to override the automatic language detection.
   * @type {string}
   */
  @property()
  locale?: string;

  /**
   * Triggers when a user chooses an option.
   */
  @event('change', true)
  private _change: EventEmitter<{ value: string }>;

  /**
   * Fires whenever dropdown expands.
   */
  @event('expand')
  private _expand: EventEmitter;

  /**
   * Fires when dropdown collapses.
   */
  @event('collapse')
  private _collapse: EventEmitter;

  @state()
  private _isExpanded = false;

  @state()
  private _triggerClickIsBubbling = false;

  @queryAssignedNodes(undefined, true, 'kat-button')
  private _defaultSlotButtons?: NodeListOf<KatButton>;

  @queryAssignedNodes('overflow', true)
  private _overflowSlot?: NodeListOf<HTMLElement>;

  static get styles() {
    return [baseStyles, styles];
  }

  constructor() {
    super();

    this._listeners = [
      {
        target: window,
        listeners: [['click', this._handleWindowClick.bind(this)]],
      },
    ];
  }

  // Safari 11 does not re-render when the queried slot nodes are available.
  // Newer versions and all other browsers are fine.
  // TODO: https://sim.amazon.com/issues/KDS-2386
  firstUpdated() {
    this.requestUpdate();
  }

  /**
   * Click events are allowed to bubble up through user provided buttons and our trigger.
   * It eventually reaches here where _triggerLastClicked will be reset OR collapsed if expanded.
   */
  private _handleWindowClick() {
    if (this._triggerClickIsBubbling) {
      this._triggerClickIsBubbling = false;
    } else if (this.expanded) {
      this.expanded = false;
    }
  }

  private get _overflowTriggerLabel(): string {
    return getString(
      `kat_button_group_overflow_trigger_label`,
      null,
      this.locale
    );
  }

  private _handleSlotChange() {
    this.requestUpdate();
  }

  private _handleFocusOut(e: MouseEvent) {
    if (
      this.expanded &&
      !this.disabled &&
      !this.contains(e.relatedTarget as Node)
    ) {
      this.expanded = false;
    }
  }

  private _handleToggleClick() {
    this.expanded = !this.expanded;
    this._triggerClickIsBubbling = true;
  }

  private _handleClick(e: MouseEvent) {
    const selectedButtonValue = (e.target as KatButton).value;

    // Target will be button-group when the overflow trigger is clicked
    // Ignore it, but let the event propogate to the global click handler will fire
    if (this._triggerClickIsBubbling) {
      return;
    }

    if (this.disabled) {
      return;
    }

    if (!selectedButtonValue) {
      // TODO: https://sim.amazon.com/issues/KDS-2364
      console.error(
        'Expected kat-button-group buttons to have a value attribute'
      );
      return;
    }

    // Always close overflow with a click, even if already selected button
    this.expanded = false;

    if (selectedButtonValue === this.value) {
      return;
    }

    this.value = selectedButtonValue;
    this._change.emit({ value: this.value });
  }

  private _setAndReturnButtonSelected(button: KatButton) {
    const selected = button.value === this.value;
    button.disabled = this.disabled;
    button.classList.toggle('selected', selected);
    return selected;
  }

  private _getHtml(
    overflowTriggerElement = nothing,
    overflowClasses = {},
    wrapperClasses = {}
  ) {
    return html`
      <div
        aria-label=${ifDefined(this.ariaLabel)}
        class="button-group-wrapper ${classMap(wrapperClasses)}"
        role="group"
        @click=${this._handleClick}
        @focusout=${this._handleFocusOut}
      >
        <slot @slotchange=${this._handleSlotChange}></slot>
        ${overflowTriggerElement}
        <div class="button-group-overflow ${classMap(overflowClasses)}">
          <slot @slotchange=${this._handleSlotChange} name="overflow"></slot>
        </div>
      </div>
    `;
  }

  render() {
    // Always apply selected/disabled to default slot buttons
    this._defaultSlotButtons?.forEach(button =>
      this._setAndReturnButtonSelected(button)
    );

    // Can only be one overflow since it's a named slot
    const overflow = this._overflowSlot?.length
      ? this._overflowSlot[0]
      : undefined;

    // No overflow, no point in calculating anything else
    if (!overflow) {
      return this._getHtml();
    }

    const btnClasses = {
      expanded: this.expanded,
      selected: false,
      'has-children': !!this._defaultSlotButtons?.length,
    };

    const overflowClasses = {
      'align-right': false,
      visible: this.expanded,
    };

    const wrapperClasses = {
      'has-overflow': true,
    };

    // Apply selected/disabled to overflow slot buttons AND remember if value matches
    overflow.querySelectorAll<KatButton>('kat-button').forEach(button => {
      if (this._setAndReturnButtonSelected(button)) {
        btnClasses.selected = true;
      }
    });

    // No point in calculating if the overflow isn't currently visible
    if (this.expanded) {
      const triggerLeftElement = this._defaultSlotButtons?.length
        ? this._defaultSlotButtons[this._defaultSlotButtons.length - 1]
        : this;
      const { left: triggerLeft } = triggerLeftElement.getBoundingClientRect();
      const { width: overflowWidth } = overflow.getBoundingClientRect();
      const { width: viewportWidth } = getViewport();

      overflowClasses['align-right'] = isPageDirectionRightToLeft()
        ? triggerLeft + overflowWidth <= viewportWidth
        : triggerLeft - overflowWidth > 0;
    }

    const overflowTriggerHtml = html`<kat-button
      aria-label="${this._overflowTriggerLabel}"
      aria-expanded=${this.expanded}
      aria-haspopup="listbox"
      part="overflow-trigger"
      class=${classMap(btnClasses)}
      ?disabled=${this.disabled}
      @click=${this._handleToggleClick}
    >
      <kat-icon name="more_horiz" size="tiny"></kat-icon>
    </kat-button>`;

    return this._getHtml(overflowTriggerHtml, overflowClasses, wrapperClasses);
  }
}
